import bloxBiService from 'services/bloxBiService';
import {
  packDownloadFilename,
  pickRenderedArtifact,
  type RenderFormat,
  triggerBlobDownload,
} from 'utils/renderExport';

import type { ArtifactCreateResponse, SnapshotCreateBody, SnapshotMetadata } from './types';

export type PackExportFormat = RenderFormat;

export interface ExportPackArtifactResult {
  snapshot: SnapshotMetadata;
  filename: string;
  /** Object URL for optional in-browser preview (PDF only). */
  previewUrl?: string;
}

const FORMAT_ERROR_LABEL: Record<PackExportFormat, string> = {
  pdf: 'PDF',
  xlsx: 'Excel',
};

/**
 * Refresh the working snapshot, render the requested format on the server, and download locally.
 */
export async function exportPackArtifact(
  dashboardId: string,
  dashboardName: string | undefined,
  context: SnapshotCreateBody,
  format: PackExportFormat,
): Promise<ExportPackArtifactResult> {
  const snapshotResponse = await bloxBiService.createDashboardSnapshot(dashboardId, {
    ...context,
    kind: 'working',
    force_refresh: true,
  });
  const snapshot: SnapshotMetadata = snapshotResponse.data;

  const artifactResponse = await bloxBiService.createDashboardSnapshotArtifacts(snapshot.id, [
    format,
  ]);
  const artifactPayload: ArtifactCreateResponse = artifactResponse.data;
  const artifact = pickRenderedArtifact(artifactPayload.artifacts, format);
  if (!artifact) {
    throw new Error(`Server did not return a ${FORMAT_ERROR_LABEL[format]} artifact`);
  }

  const blobResponse = await bloxBiService.downloadDashboardSnapshotArtifact(
    artifact.download_path,
  );
  const blob = blobResponse.data as Blob;
  const filename = packDownloadFilename(dashboardName, format);
  triggerBlobDownload(blob, filename);

  const previewUrl = format === 'pdf' ? URL.createObjectURL(blob) : undefined;

  return { snapshot, filename, previewUrl };
}

/**
 * Refresh the working snapshot, render PDF on the server, and download locally.
 */
export async function exportPackPdf(
  dashboardId: string,
  dashboardName: string | undefined,
  context: SnapshotCreateBody,
): Promise<ExportPackArtifactResult & { previewUrl: string }> {
  const result = await exportPackArtifact(dashboardId, dashboardName, context, 'pdf');
  if (!result.previewUrl) {
    throw new Error('Server did not return a PDF artifact');
  }
  return { ...result, previewUrl: result.previewUrl };
}
